import { Fragment, useMemo, useState } from 'react';
import { Badge, Card, Collapse, Table } from 'react-bootstrap';
import { readPDCFindingsEvidence, type PDCFindingEvidence, type PDCRun } from './pdc.utils';

function riskLevelBadge(level: unknown) {
  const s = String(level ?? '')
    .trim()
    .toUpperCase();
  if (s === 'HIGH' || s === 'CRITICAL') return <Badge bg="danger">{s || '—'}</Badge>;
  if (s === 'MEDIUM' || s === 'MODERATE') {
    return (
      <Badge bg="warning" text="dark">
        {s}
      </Badge>
    );
  }
  if (s === 'LOW') return <Badge bg="success">{s}</Badge>;
  return <Badge bg="secondary">{s || '—'}</Badge>;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function HighlightedEvidence({ text, keywords }: { text: string; keywords: string[] }) {
  const uniqueKeywords = [...new Set(keywords.filter(k => k && k.trim().length > 0))];
  if (!text || uniqueKeywords.length === 0) return <span>{text || '—'}</span>;

  const pattern = new RegExp(`(${uniqueKeywords.map(escapeRegExp).join('|')})`, 'gi');
  const parts = text.split(pattern);

  return (
    <span>
      {parts.map((part, i) =>
        uniqueKeywords.some(k => k.toLowerCase() === part.toLowerCase()) ? (
          <mark key={i} className="bg-warning-subtle px-1 rounded">
            {part}
          </mark>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        )
      )}
    </span>
  );
}

export function PDCFindingsTable({ run }: { run: PDCRun | null | undefined }) {
  const evidence = useMemo(() => readPDCFindingsEvidence(run), [run]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (evidence.length === 0) {
    return (
      <Card className="border-0 shadow-sm mt-3">
        <Card.Body>
          <h6 className="mb-1">Detailed Findings &amp; Evidence</h6>
          <p className="small text-muted mb-0">No findings recorded for this evaluation run.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm mt-3">
      <Card.Body>
        <h6 className="mb-1">Detailed Findings &amp; Evidence</h6>
        <p className="small text-muted mb-3">Expand a row for evidence excerpts, root cause, and remediation.</p>
        <div className="table-responsive">
          <Table bordered hover size="sm" className="align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th style={{ width: '8%' }} />
                <th>Finding ID</th>
                <th>Source</th>
                <th>Attack Type</th>
                <th>OWASP</th>
                <th>MITRE</th>
                <th>Risk Level</th>
              </tr>
            </thead>
            <tbody>
              {evidence.map((row: PDCFindingEvidence, index: number) => {
                const isOpen = openIndex === index;
                const keywords = Array.isArray(row.evidence) ? row.evidence.map(String) : [];
                return (
                  <Fragment key={String(row.finding_id ?? index)}>
                    <tr
                      role="button"
                      tabIndex={0}
                      className={isOpen ? 'table-active' : undefined}
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenIndex(isOpen ? null : index);
                        }
                      }}
                    >
                      <td className="text-center text-muted">{isOpen ? '▼' : '▶'}</td>
                      <td className="fw-medium">{row.finding_id ?? '—'}</td>
                      <td>{row.source ?? '—'}</td>
                      <td>{row.attack_type ?? row.component ?? '—'}</td>
                      <td>
                        <code className="small">{row.owasp_mapping ?? '—'}</code>
                      </td>
                      <td>
                        <code className="small">{row.mitre_mapping ?? '—'}</code>
                      </td>
                      <td>{riskLevelBadge(row.risk_level)}</td>
                    </tr>
                    <tr>
                      <td colSpan={7} className="p-0 border-0">
                        <Collapse in={isOpen}>
                          <div className="p-3 bg-body-tertiary border-top">
                            <div className="mb-3">
                              <strong className="small d-block mb-1">Evidence Excerpt</strong>
                              <p className="small mb-0 font-monospace">
                                <HighlightedEvidence text={String(row.evidence_text ?? '')} keywords={keywords} />
                              </p>
                            </div>
                            {row.root_cause?.description ? (
                              <div className="mb-3">
                                <strong className="small d-block mb-1">Root Cause</strong>
                                <p className="small mb-0">{row.root_cause.description}</p>
                              </div>
                            ) : null}
                            {Array.isArray(row.remediation_steps) && row.remediation_steps.length > 0 ? (
                              <div>
                                <strong className="small d-block mb-1">Remediation Steps</strong>
                                <ol className="small mb-0 ps-3">
                                  {row.remediation_steps.map((step, i) => (
                                    <li key={i}>{step}</li>
                                  ))}
                                </ol>
                              </div>
                            ) : null}
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}
